import React, { useEffect, useState } from "react";
import {
  SelectChangeEvent,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAddNotificationMutation } from "../../service/notifications";
import { useLazyGetUsersQuery } from "../../service/users";
import { showError } from "../../constants/toasts";

interface NotificationForm {
  type: string;
  title: string;
  subject: string;
  content: string;
  broadcastReceivers: string[];
  isSendToAll: boolean;
}

interface UserOption {
  label: string;
  id?: string;
}

const initialFormState: NotificationForm = {
  type: "default",
  title: "",
  subject: "",
  content: "",
  broadcastReceivers: [],
  isSendToAll: false,
};

const AddNotification = () => {
  const navigate = useNavigate();
  const [getUserList] = useLazyGetUsersQuery();
  const [addNotification, { isLoading }] = useAddNotificationMutation();
  const [formData, setFormData] = useState<NotificationForm>(initialFormState);
  const [usersList, setUsersList] = useState<UserOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    getUsers();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    if (event.target.value !== "default") {
      setFormData((prev) => ({ ...prev, type: event.target.value as string }));
    }
  };

  const handleUsersChange = (event: any, value: UserOption[]) => {
    setSelectedUsers(value);
    setFormData((prev) => ({
      ...prev,
      broadcastReceivers: value
        .map((user) => user.id)
        .filter((id): id is string => id !== undefined),
      isSendToAll: value.length === usersList.length,
    }));
  };

  const getUsers = async (p?: number) => {
    try {
      const response = await getUserList({
        page: 1,
        limit: 1000,
      }).unwrap();
      if (response.code === 200) {
        const data = response.data.list;
        const users = data.map((user: any) => ({
          label: `${user?.firstName ? user?.firstName : ""} ${
            user?.lastName ? user?.lastName : ""
          } ${user?.email ? user?.email : ""}`,
          id: user?._id,
        }));
        setUsersList(users);
      }
    } catch (error: any) {
      showError(error.data.message || "");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }
    if (!formData.content.trim()) {
      setError("Message content is required");
      return;
    }

    try {
      const response = await addNotification({
        ...formData,
        // broadcastReceivers: formData.isSendToAll
        //   ? selectedUsers
        //       .map((user) => user.id)
        //       .filter((id): id is string => id !== undefined)
        //   : formData.broadcastReceivers,
      }).unwrap();

      if (response) {
        setOpenSnackbar(true);
        setTimeout(() => navigate("/manage-notifications"), 1000);
      }
    } catch (err: any) {
      setError(err.data?.message || "Failed to send notification");
      console.error("Error adding notification:", err);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <>
      <div className="main_title">
        <h1>Add Notification</h1>
        <p>
          <button type="button" onClick={() => navigate("/dashboard")}>
            Dashboard
          </button>{" "}
          -{" "}
          <button
            type="button"
            onClick={() => navigate("/manage-notifications")}
          >
            Notification Management
          </button>{" "}
          - Add Notification
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={handleSubmit}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <div className="gap_p">
            <div className="control_group w_50">
              <label htmlFor="title">Notification Title *</label>
              <TextField
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter Notification Title"
                fullWidth
                required
                disabled={isLoading}
              />
            </div>

            <div className="control_group w_50">
              <label htmlFor="type">Type *</label>
              <Select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleTypeChange}
                fullWidth
                required
                disabled={isLoading}
                defaultValue="default"
              >
                <MenuItem value="default">Select Type</MenuItem>
                <MenuItem value="PUSH">Broadcast</MenuItem>
                <MenuItem value="EMAIL">Email</MenuItem>
              </Select>
            </div>

            {formData.type === "EMAIL" && (
              <div className="control_group w_100">
                <label htmlFor="subject">Email Subject *</label>
                <TextField
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="Enter Email Subject"
                  fullWidth
                  required={formData.type === "EMAIL"}
                  disabled={isLoading}
                />
              </div>
            )}

            <div className="control_group w_100">
              <label htmlFor="audience">
                {formData.isSendToAll ? "All Users" : "Select Recipients"}
              </label>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={usersList}
                value={selectedUsers}
                onChange={handleUsersChange}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={isLoading || formData.isSendToAll}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <FormControlLabel
                      control={<Checkbox checked={selected} />}
                      label={option.label}
                    />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="audience"
                    placeholder={
                      formData.isSendToAll
                        ? "All users will receive this notification"
                        : "Select recipients..."
                    }
                  />
                )}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isSendToAll}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        isSendToAll: isChecked,
                        broadcastReceivers: isChecked
                          ? usersList
                              .map((user) => user.id)
                              .filter((id): id is string => id !== undefined)
                          : selectedUsers
                              .map((user) => user.id)
                              .filter((id): id is string => id !== undefined),
                      }));
                      if (isChecked) {
                        setSelectedUsers(usersList);
                      } else {
                        setSelectedUsers([]);
                      }
                    }}
                    disabled={isLoading}
                  />
                }
                label="Send to all users"
              />
            </div>

            <div className="control_group w_100">
              <label htmlFor="content">
                {formData.type === "email"
                  ? "Email Content *"
                  : "Message Content *"}
              </label>
              <TextField
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder={
                  formData.type === "email"
                    ? "Enter your email content here..."
                    : "Enter your notification message here..."
                }
                fullWidth
                multiline
                minRows={5}
                maxRows={10}
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary"
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? "Sending..." : "Send Notification"}
            </Button>
          </div>
        </form>
      </div>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Notification sent successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddNotification;
